import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getAuthPersonSelector } from '@redux/oauth/oauthSelectors';
import { setSelectedAssessment } from '@redux/tasks/tasksActions';
import { getAssessmentsListSelector } from '@redux/tasks/tasksSelector';
import { getAssessmentsListThunk } from '@redux/tasks/tasksThunks';
import { AssessmentListItemType } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { EmptyListSmallArea } from 'components/EmptyListSmallArea/EmptyListSmallArea';
import { NotepadIcon } from 'components/Icons';
import { SearchField } from 'components/SearchField/SearchField';
import { AssessmentMenuOption } from 'containers/Tasks/components/AssessmentArea/AssessmentMenuOption/AssessmentMenuOption';
import { Color } from 'utils/color';
import style from './AssessmentSearch.scss';

type Props = ConnectedProps<typeof connectRedux>;

export const AssessmentSearch: React.FC<Props> = ({ authPersonSelector, fetchAssessmentsList, setAssessment, assessmentsList }) => {
  const organizationId = authPersonSelector?.organizationId;
  const [searchText, setSearchText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean | null>(null);
  const [filteredAssessments, setFilteredAssessments] = useState<AssessmentListItemType[]>([]);

  useEffect(() => {
    if (!assessmentsList?.length && organizationId) {
      setIsLoading(true);
      fetchAssessmentsList(organizationId).finally(() => setIsLoading(false));
    }
  }, [organizationId]);

  useEffect(() => {
    if (assessmentsList?.length) {
      const searchResult = assessmentsList.filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()));
      setFilteredAssessments(searchResult);
    } else {
      setFilteredAssessments([]);
    }
  }, [assessmentsList, searchText, setFilteredAssessments]);

  const setUnifiedSearchText = useCallback((text: string) => {
    setSearchText(text?.trim());
  }, []);

  return (
    <>
      <SearchField searchText={searchText} setSearchText={setUnifiedSearchText} placeholder="Search" />
      {!!filteredAssessments?.length && (
        <div className={style.resultsContainer}>
          {filteredAssessments?.map((option, index) => (
            <AssessmentMenuOption option={option} onClick={() => setAssessment(option)} key={index} containerStyle={style.resultsItem} />
          ))}
        </div>
      )}
      {!!assessmentsList?.length && !filteredAssessments?.length && !!searchText.trim() && (
        <EmptyListSmallArea icon={() => <SearchIcon sx={{ color: Color.GRAY_BLUE }} />} text="No matches found" containerStyle={style.marginBottom} />
      )}
      {isLoading ? (
        <div className={style.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        isLoading === false && !assessmentsList?.length && <EmptyListSmallArea icon={NotepadIcon} text="Add assessment" containerStyle={style.marginBottom} />
      )}
    </>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    authPersonSelector: getAuthPersonSelector(state),
    assessmentsList: getAssessmentsListSelector(state)
  }),
  {
    fetchAssessmentsList: getAssessmentsListThunk,
    setAssessment: setSelectedAssessment
  }
);

export default compose(connectRedux)(AssessmentSearch);
